import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import _extends from '@babel/runtime/helpers/extends';
import set from 'lodash/set';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import pick from 'lodash/pick';
import keys from 'lodash/keys';
import cloneDeep from 'lodash/cloneDeep';
import React, { useContext, useRef, useReducer, useEffect } from 'react';
var FormContext = React.createContext();

var useForm = function useForm() {
  var context = useContext(FormContext);
  return context;
};

var FormConsumer = FormContext.Consumer;

function reducer(state, action) {
  switch (action.type) {
    case 'update':
      return _extends({}, state, action.payload);
  }
}

var FormProvider = function FormProvider(_ref) {
  var initialValues = _ref.initialValues,
      onChange = _ref.onChange,
      validate = _ref.validate,
      onSubmit = _ref.onSubmit,
      children = _ref.children,
      formProps = _objectWithoutPropertiesLoose(_ref, ["initialValues", "onChange", "validate", "onSubmit", "children"]);

  var fields = useRef({});

  var _useReducer = useReducer(reducer, {
    values: cloneDeep(initialValues),
    touched: {},
    errors: {},
    submitting: false,
    validated: false
  }),
      state = _useReducer[0],
      dispatch = _useReducer[1];

  var values = state.values,
      touched = state.touched,
      errors = state.errors,
      submitting = state.submitting,
      validated = state.validated;

  function _setValues(values) {
    dispatch({
      type: 'update',
      payload: {
        values: values
      }
    });
  }

  function setTouched(touched) {
    dispatch({
      type: 'update',
      payload: {
        touched: touched
      }
    });
  }

  function _setErrors(errors) {
    dispatch({
      type: 'update',
      payload: {
        errors: errors
      }
    });
  }

  function setSubmitting(submitting) {
    dispatch({
      type: 'update',
      payload: {
        submitting: submitting
      }
    });
  }

  function setValidated(validated) {
    dispatch({
      type: 'update',
      payload: {
        validated: validated
      }
    });
  }

  var setValue = function setValue(name, value) {
    var newValues = set(_extends({}, values), name, value);

    _setValues(newValues);

    return newValues;
  };

  var getValue = function getValue(name) {
    return get(values, name);
  };

  var getError = function getError(name) {
    return errors[name];
  };

  var hasError = function hasError(name) {
    return !isEmpty(getError(name));
  };

  function registerField(name, field) {
    fields.current[name] = field;
  }

  var props = _extends({}, formProps, {
    setValue: setValue,
    getValue: getValue,
    getError: getError,
    hasError: hasError,
    setValues: function setValues(values) {
      _setValues(values);

      return values;
    },
    setErrors: function setErrors(errors) {
      _setErrors(errors);

      return errors;
    },
    setTouched: setTouched,
    setSubmitting: setSubmitting,
    values: values,
    errors: errors,
    touched: touched,
    submitting: submitting,
    handleChange: handleChange,
    handleFocus: handleFocus,
    handleBlur: handleBlur,
    handleSubmit: handleSubmit,
    registerField: registerField
  });

  function validateForm(values, touched, validateAll, cb) {
    if (validateAll === void 0) {
      validateAll = false;
    }

    if (cb === void 0) {
      cb = function cb() {};
    }

    var fn = validate(values, props);

    if (fn && fn.then) {
      fn.then(function (errors) {
        updateErrors(errors);
      });
    } else {
      updateErrors(fn);
    }

    function updateErrors(errors) {
      if (!isEmpty(errors)) {
        Object.keys(errors).forEach(function (k) {
          if (!touched[k] && !validateAll && !validated) {
            delete errors[k];
          }
        });

        _setErrors(errors);

        return cb(false);
      } else {
        _setErrors({});

        return cb(true);
      }
    }
  }

  function handleChange(value, name) {
    if (name) {
      var newValues = onChange(name, value, props);

      if (validate) {
        validateForm(newValues, touched, false);
      }
    }
  }

  function handleFocus() {}

  function handleBlur(name) {
    var _extends2;

    setTouched(_extends({}, touched, (_extends2 = {}, _extends2[name] = true, _extends2)));

    if (validate) {
      var _extends3;

      validateForm(values, _extends({}, touched, (_extends3 = {}, _extends3[name] = true, _extends3)), false);
    }
  }

  function handleSubmit(action) {
    if (action === void 0) {
      action = 'submit';
    }

    if (submitting) {
      return;
    }

    if (validate) {
      setValidated(true);
      validateForm(values, touched, true, function (success) {
        if (success) {
          submitForm();
        }
      });
    } else {
      submitForm();
    }

    function submitForm() {
      setSubmitting(action);

      if (onSubmit) {
        onSubmit(pick(values, keys(fields.current)), _extends({}, props, {
          action: action
        }));
      }
    }
  }

  if (isFunction(children)) {
    return /*#__PURE__*/React.createElement(FormContext.Provider, {
      value: props
    }, children(props));
  }

  return /*#__PURE__*/React.createElement(FormContext.Provider, {
    value: props
  }, children);
};

FormProvider.defaultProps = {
  initialValues: {},
  onChange: function onChange(name, value, _ref2) {
    var setValue = _ref2.setValue;
    return setValue(name, value);
  }
};

var Form = function Form(_ref) {
  var children = _ref.children,
      formProps = _ref.formProps,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "formProps"]);

  return /*#__PURE__*/React.createElement(FormProvider, props, function (_ref2) {
    var handleSubmit = _ref2.handleSubmit;
    return /*#__PURE__*/React.createElement("form", _extends({}, formProps, {
      onSubmit: function onSubmit(e) {
        e.preventDefault();
        handleSubmit('submit');
      }
    }), children);
  });
};

var FormControl = function FormControl(_ref) {
  var component = _ref.component,
      name = _ref.name,
      props = _objectWithoutPropertiesLoose(_ref, ["component", "name"]);

  var _useForm = useForm(),
      handleChange = _useForm.handleChange,
      handleFocus = _useForm.handleFocus,
      handleBlur = _useForm.handleBlur,
      getValue = _useForm.getValue,
      registerField = _useForm.registerField; // const invalid = hasError(name);


  useEffect(function () {
    registerField(name);
  }, [name]);
  return /*#__PURE__*/React.createElement(component, _extends({}, props, {
    id: name,
    name: name,
    // invalid,
    value: getValue(name),
    onChange: function onChange(x) {
      return handleChange(x.target ? x.target.value : x, name);
    },
    onFocus: function onFocus() {
      return handleFocus(name);
    },
    onBlur: function onBlur() {
      return handleBlur(name);
    }
  }));
};

FormControl.defaultProps = {
  component: 'input'
};

var FormSubmit = function FormSubmit(_ref) {
  var action = _ref.action,
      children = _ref.children,
      component = _ref.component,
      props = _objectWithoutPropertiesLoose(_ref, ["action", "children", "component"]);

  var _useForm = useForm(),
      submitting = _useForm.submitting,
      handleSubmit = _useForm.handleSubmit;

  var type = action === 'submit' ? 'submit' : 'button';

  var elProps = _extends({}, props, {
    type: type,
    disabled: action === submitting
  });

  if (action !== 'submit') {
    elProps.onClick = function () {
      return handleSubmit(action);
    };
  }

  return /*#__PURE__*/React.createElement(component, elProps, children);
};

FormSubmit.defaultProps = {
  action: 'submit',
  type: 'submit',
  component: 'button',
  children: 'Submit'
};

var FormLabel = function FormLabel(_ref) {
  var name = _ref.name,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["name", "children"]);

  return /*#__PURE__*/React.createElement("label", _extends({
    htmlFor: name
  }, props), children || name);
};

var FormError = function FormError(_ref) {
  var name = _ref.name,
      props = _objectWithoutPropertiesLoose(_ref, ["name"]);

  var _useForm = useForm(),
      hasError = _useForm.hasError,
      getError = _useForm.getError;

  return hasError(name) ? /*#__PURE__*/React.createElement("div", props, getError(name)) : null;
};

var FormSubmitButton = function FormSubmitButton(_ref) {
  var onSubmit = _ref.onSubmit,
      component = _ref.component,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["onSubmit", "component", "children"]);

  function handleSubmit(values) {
    if (onSubmit) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      onSubmit.apply(void 0, args);
    }
  }

  return /*#__PURE__*/React.createElement(FormProvider, {
    onSubmit: handleSubmit
  }, function (_ref2) {
    var handleSubmit = _ref2.handleSubmit;
    return /*#__PURE__*/React.createElement(component, _extends({}, props, {
      onClick: function onClick() {
        return handleSubmit('submit');
      }
    }), children);
  });
};

FormSubmitButton.defaultProps = {
  component: 'button'
};
export { Form, FormConsumer, FormContext, FormControl, FormError, FormLabel, FormProvider, FormSubmit, FormSubmitButton, useForm };